import { useStackOneHub } from '@stackone/react-hub'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { PayloadCepIntegration } from 'common/api'
import type { IntegrationProvider } from 'common/api/integrationsApi'
import { getIntegrationName } from 'common/cep/cepUtils'
import PageContainer from 'common/components/PageContainer'
import { useAppSelector } from 'common/hooks/redux'
import { getSelectedAccountName } from 'features/auth/store/authSlice'

import useCreateCepIntegration from '../../api/mutations/useCreateCepIntegration'
import useSetupIntegrationConnection from '../../api/mutations/useSetupIntegrationConnection'
import { CepTemplateFormatted } from '../../api/queries/useGetCepIntegrationTemplatesQuery'

import IntegrationCardCepIntegration from './integrationCard/IntegrationCardCepIntegration'
import ProviderIntegrationCards from './integrationCard/IntegrationCardProvider'

type Props = {
  integrations?: CepTemplateFormatted[] | IntegrationProvider[]
  onSuccess: () => void
}

const IntegrationsList = ({ integrations, onSuccess }: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const {
    simplifiedProjectIntegrations: isSimplifiedProjectIntegrationOn,
    s1NameIntegration,
  } = useFlags()
  const { mutate, isLoading } = useSetupIntegrationConnection()
  const { mutate: mutateIntegrationCep } = useCreateCepIntegration(accountId)

  const { startConnect } = useStackOneHub()
  const accountName = useAppSelector(getSelectedAccountName)
  const firstName = useAppSelector((state) => state.authStates.firstName)
  const lastName = useAppSelector((state) => state.authStates.lastName)

  const onClick = async (id: string, label: string, onClose: () => void) => {
    if (accountName) {
      mutate(
        {
          provider: id,
          accountId,
          accountName,
          userName: `${firstName} ${lastName}`,
          label,
        },
        {
          onSuccess: (sessionToken) => {
            onClose?.()
            startConnect({
              sessionToken,
              onSuccess,
            })
          },
        }
      )
    }
  }

  const onClickCepIntegration = (payload: PayloadCepIntegration) => {
    mutateIntegrationCep(
      {
        payload,
        accountId,
      },
      {
        onSuccess: () => {
          onSuccess?.()
        },
      }
    )
  }

  return (
    <PageContainer className="max-w-318 sm:mx-auto pt-10 px-6 justify-start">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-6">
        {isSimplifiedProjectIntegrationOn
          ? (integrations as CepTemplateFormatted[] | undefined)?.map(
              (integration) => (
                <IntegrationCardCepIntegration
                  cep={integration}
                  key={integration.id}
                  name={getIntegrationName(integration.cepName)}
                  className="px-6 pb-3 pt-4"
                  onSubmit={onClickCepIntegration}
                />
              )
            )
          : (integrations as IntegrationProvider[] | undefined)?.map(
              (integration) => (
                <ProviderIntegrationCards
                  key={integration.id}
                  name={integration.name}
                  logoUrl={integration.logoUrl!}
                  id={integration.id}
                  isS1NameEnabled={s1NameIntegration}
                  onSubmit={onClick}
                  isLoading={isLoading}
                />
              )
            )}
      </div>
    </PageContainer>
  )
}

export default IntegrationsList
